/**
 * src/features/learning/components/AIReviewPanel.tsx
 *
 * MER Algo Footprints v7.1H — AI Review Panel
 *
 * Journal draft state machine:
 *   idle       → review not yet generated
 *   reviewing  → AI generating review
 *   draft      → journal draft ready, awaiting user decision
 *   editing    → user is editing the draft text
 *   accepted   → user accepted; snapshot patched with journal_status='accepted'
 *   discarded  → user discarded; snapshot patched with journal_status='discarded'
 *
 * Minimal required user inputs before accepting:
 *   1. Did you take it? (user_action: entered | passed | watching)
 *   2. If not, why not? (pass_reason — shown only when user_action = passed)
 *   3. Did you follow the plan? (followed_plan: yes | partial | no)
 */

import React, { useState, useCallback } from 'react';
import type { LearningSnapshot, AIReview, WindowEval } from '../hooks/useLearningData';

// ── Journal draft state ───────────────────────────────────────────────────────

type JournalState = 'idle' | 'reviewing' | 'draft' | 'editing' | 'accepted' | 'discarded';

interface JournalInputs {
  user_action:   'entered' | 'passed' | 'watching' | null;
  pass_reason:   string;
  followed_plan: 'yes' | 'partial' | 'no' | null;
}

// ── Style helpers ─────────────────────────────────────────────────────────────

const mono: React.CSSProperties = { fontFamily: 'var(--mono)' };

const lbl = (_text: string): React.CSSProperties => ({
  ...mono, fontSize: 9, color: 'var(--blue-dim)',
  textTransform: 'uppercase' as const, letterSpacing: '.5px',
  display: 'block', marginBottom: 3,
});

// v1 enum values — must match aiReviewEngine.js
const QUALITY_COLOR: Record<string, string> = {
  correct: 'var(--green)', early: 'var(--amber)', late: 'var(--amber)', wrong: 'var(--red)',
  high: 'var(--green)', medium: 'var(--cyan)', low: 'var(--red)',
  aligned: 'var(--green)', partial: 'var(--amber)', deviated: 'var(--red)',
  accurate: 'var(--green)', overestimated: 'var(--amber)', underestimated: 'var(--cyan)',
  structure: 'var(--cyan)', liquidity: 'var(--cyan)', trap: 'var(--amber)', gamma: 'var(--blue-dim)',
  none: 'var(--green)', timing: 'var(--amber)', structure_break: 'var(--red)',
};

const CLASSIFICATION_COLOR: Record<string, string> = {
  correct_thesis_correct_execution: 'var(--green)',
  correct_thesis_poor_execution:    'var(--cyan)',
  wrong_thesis_disciplined_pass:    'var(--cyan)',
  correct_avoid:                    'var(--cyan)',
  valid_setup_no_trigger:           'var(--blue-dim)',
  wrong_thesis_wrong_execution:     'var(--red)',
  false_positive:                   'var(--red)',
  rapid_invalidation:               'var(--amber)',
  missed_opportunity:               'var(--amber)',
};

const CLASSIFICATION_LABEL: Record<string, string> = {
  correct_thesis_correct_execution: '✓ Correct Thesis + Execution',
  correct_thesis_poor_execution:    '△ Correct Thesis / Poor Execution',
  wrong_thesis_disciplined_pass:    '◎ Wrong Thesis / Disciplined Pass',
  correct_avoid:                    '◎ Correct Avoid',
  valid_setup_no_trigger:           '○ Valid Setup / No Trigger',
  wrong_thesis_wrong_execution:     '✗ Wrong Thesis + Wrong Execution',
  false_positive:                   '✗ False Positive',
  rapid_invalidation:               '⚡ Rapid Invalidation',
  missed_opportunity:               '◈ Missed Opportunity',
};

function Badge({ value, colorMap }: { value: string; colorMap: Record<string, string> }) {
  const color = colorMap[value] || 'var(--blue-dim)';
  return (
    <span style={{
      ...mono, fontSize: 10, padding: '2px 7px', borderRadius: 3,
      border: `1px solid ${color}`, color, background: `${color}18`,
    }}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

function SectionHead({ icon, title }: { icon: string; title: string }) {
  return (
    <div style={{
      ...mono, fontSize: 9, color: 'var(--gold)',
      textTransform: 'uppercase' as const, letterSpacing: '1px',
      marginBottom: 8, marginTop: 14,
      borderBottom: '1px solid var(--border)', paddingBottom: 4,
    }}>
      {icon} {title}
    </div>
  );
}

function OutcomeWindows({ windows }: { windows: Record<string, WindowEval> }) {
  const order = ['check@5m', 'check@15m', 'check@30m', 'check@60m', 'session_close'];
  const present = order.filter(k => windows[k]);
  if (!present.length) return null;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, ...mono }}>
        <thead>
          <tr style={{ color: 'var(--blue-dim)', borderBottom: '1px solid var(--border)' }}>
            {['Window', 'Price', 'MFE', 'MAE', 'Inv', 'T1'].map(h => (
              <th key={h} style={{ padding: '2px 8px', textAlign: 'left', fontWeight: 400, fontSize: 9 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {present.map(w => {
            const ev = windows[w];
            return (
              <tr key={w} style={{ borderBottom: '1px solid var(--border-faint, var(--border))' }}>
                <td style={{ padding: '3px 8px', color: 'var(--cyan)' }}>{w}</td>
                <td style={{ padding: '3px 8px' }}>${ev.price_at_window?.toFixed(2) ?? '—'}</td>
                <td style={{ padding: '3px 8px', color: (ev.mfe || 0) > 0 ? 'var(--green)' : 'var(--blue-dim)' }}>
                  {ev.mfe != null ? `+${ev.mfe.toFixed(3)}` : '—'}
                </td>
                <td style={{ padding: '3px 8px', color: (ev.mae || 0) > 0 ? 'var(--red)' : 'var(--blue-dim)' }}>
                  {ev.mae != null ? `-${ev.mae.toFixed(3)}` : '—'}
                </td>
                <td style={{ padding: '3px 8px', color: ev.invalidated ? 'var(--red)' : 'var(--green)' }}>
                  {ev.invalidated ? 'YES' : 'no'}
                </td>
                <td style={{ padding: '3px 8px', color: ev.target_hit?.t1 ? 'var(--green)' : 'var(--blue-dim)' }}>
                  {ev.target_hit?.t1 ? '✓' : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Journal input form ────────────────────────────────────────────────────────

function JournalInputForm({
  inputs, onChange,
}: {
  inputs: JournalInputs;
  onChange: (patch: Partial<JournalInputs>) => void;
}) {
  const btnBase: React.CSSProperties = {
    ...mono, fontSize: 10, padding: '3px 10px', borderRadius: 3,
    cursor: 'pointer', marginRight: 6, marginBottom: 4,
  };
  const chip = (
    label: string,
    active: boolean,
    onClick: () => void,
    activeColor = 'var(--cyan)'
  ) => (
    <button key={label} onClick={onClick} style={{
      ...btnBase,
      border: `1px solid ${active ? activeColor : 'var(--border)'}`,
      background: active ? `${activeColor}22` : 'var(--surface2)',
      color: active ? activeColor : 'var(--blue-dim)',
    }}>{label}</button>
  );

  return (
    <div>
      {/* Q1: Did you take it? */}
      <div style={{ marginBottom: 10 }}>
        <span style={lbl('Did you take it?')} />
        {chip('● Entered', inputs.user_action === 'entered', () => onChange({ user_action: 'entered' }), 'var(--green)')}
        {chip('◎ Passed',  inputs.user_action === 'passed',  () => onChange({ user_action: 'passed'  }), 'var(--cyan)')}
        {chip('◈ Watching',inputs.user_action === 'watching',() => onChange({ user_action: 'watching'}), 'var(--amber)')}
      </div>

      {/* Q2: If passed — why? */}
      {inputs.user_action === 'passed' && (
        <div style={{ marginBottom: 10 }}>
          <span style={lbl('Why did you pass?')} />
          <input
            type="text"
            value={inputs.pass_reason}
            onChange={e => onChange({ pass_reason: e.target.value })}
            placeholder="e.g. setup not confirmed, wrong regime, risk limit"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--surface2)', border: '1px solid var(--border)',
              color: 'var(--text)', ...mono, fontSize: 11,
              padding: '4px 8px', borderRadius: 3,
            }}
          />
        </div>
      )}

      {/* Q3: Did you follow the plan? */}
      <div style={{ marginBottom: 10 }}>
        <span style={lbl('Did you follow the plan?')} />
        {chip('Yes',     inputs.followed_plan === 'yes',     () => onChange({ followed_plan: 'yes'     }), 'var(--green)')}
        {chip('Partial', inputs.followed_plan === 'partial', () => onChange({ followed_plan: 'partial' }), 'var(--amber)')}
        {chip('No',      inputs.followed_plan === 'no',      () => onChange({ followed_plan: 'no'      }), 'var(--red)')}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export interface AIReviewPanelProps {
  snapshot:        LearningSnapshot;
  onTriggerReview: (id: string) => Promise<AIReview | null>;
  onUserAction:    (id: string, action: 'entered' | 'passed' | 'watching' | null) => Promise<boolean>;
  onJournalAccept: (id: string, inputs: JournalInputs, draftText: string) => Promise<boolean>;
  onJournalDiscard:(id: string) => Promise<boolean>;
  reviewLoading?:  boolean;
  reviewError?:    string | null;
}

export function AIReviewPanel({
  snapshot,
  onTriggerReview,
  onUserAction,
  onJournalAccept,
  onJournalDiscard,
  reviewLoading = false,
  reviewError = null,
}: AIReviewPanelProps) {
  const review       = snapshot.ai_review;
  const outcome      = snapshot.outcome_eval;
  const classification = outcome?.classification;

  // Journal state machine
  const initialState: JournalState =
    (snapshot as any).journal_status === 'accepted'  ? 'accepted'  :
    (snapshot as any).journal_status === 'discarded' ? 'discarded' :
    review ? 'draft' : 'idle';

  const [journalState,  setJournalState]  = useState<JournalState>(initialState);
  const [draftText,     setDraftText]     = useState<string>(review?.journal_draft ?? '');
  const [inputs,        setInputs]        = useState<JournalInputs>({
    user_action:   snapshot.user_action,
    pass_reason:   '',
    followed_plan: null,
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const patchInputs = useCallback((patch: Partial<JournalInputs>) => {
    setInputs(prev => ({ ...prev, ...patch }));
    // Also sync user_action to the server immediately
    if (patch.user_action !== undefined) {
      onUserAction(snapshot.snapshot_id, patch.user_action);
    }
  }, [snapshot.snapshot_id, onUserAction]);

  const handleAccept = useCallback(async () => {
    setSaving(true);
    const ok = await onJournalAccept(snapshot.snapshot_id, inputs, draftText);
    if (ok) setJournalState('accepted');
    setSaving(false);
  }, [snapshot.snapshot_id, inputs, draftText, onJournalAccept]);

  const handleDiscard = useCallback(async () => {
    setSaving(true);
    const ok = await onJournalDiscard(snapshot.snapshot_id);
    if (ok) setJournalState('discarded');
    setSaving(false);
  }, [snapshot.snapshot_id, onJournalDiscard]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(draftText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [draftText]);

  const btnBase: React.CSSProperties = {
    ...mono, fontSize: 10, padding: '3px 10px', borderRadius: 3,
    cursor: 'pointer', border: '1px solid var(--border)',
    background: 'var(--surface2)', color: 'var(--text)',
  };

  return (
    <div style={{ ...mono, fontSize: 11, color: 'var(--text)', padding: '8px 0' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)' }}>{snapshot.symbol}</span>
        <span style={{ fontSize: 9, color: 'var(--blue-dim)' }}>
          {new Date(snapshot.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET
        </span>
        {classification && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 3,
            color: CLASSIFICATION_COLOR[classification] || 'var(--blue-dim)',
            border: `1px solid ${CLASSIFICATION_COLOR[classification] || 'var(--border)'}`,
            background: `${CLASSIFICATION_COLOR[classification] || 'var(--border)'}18`,
          }}>
            {CLASSIFICATION_LABEL[classification] || classification}
          </span>
        )}
        {/* Journal status badge */}
        {journalState === 'accepted'  && <span style={{ fontSize: 9, color: 'var(--green)',    border: '1px solid var(--green)',    padding: '1px 6px', borderRadius: 3 }}>✓ ACCEPTED</span>}
        {journalState === 'discarded' && <span style={{ fontSize: 9, color: 'var(--blue-dim)', border: '1px solid var(--border)',   padding: '1px 6px', borderRadius: 3 }}>✕ DISCARDED</span>}
      </div>

      {/* ── Snapshot summary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px 14px', marginBottom: 6 }}>
        {snapshot.bias       && <div><span style={lbl('Bias')} /><span style={{ fontSize: 11, color: snapshot.bias === 'bullish' ? 'var(--green)' : snapshot.bias === 'bearish' ? 'var(--red)' : 'var(--text)' }}>{snapshot.bias}</span></div>}
        {snapshot.setup_type && <div><span style={lbl('Setup')} /><span style={{ fontSize: 11, color: 'var(--cyan)' }}>{snapshot.setup_type.replace(/_/g, ' ')}</span></div>}
        {snapshot.confidence  != null && <div><span style={lbl('Confidence')} /><span style={{ fontSize: 11 }}>{snapshot.confidence}</span></div>}
        {snapshot.regime_label && <div><span style={lbl('Regime')} /><span style={{ fontSize: 11 }}>{snapshot.regime_label}</span></div>}
      </div>

      {/* ── Outcome windows ── */}
      {outcome?.windows && Object.keys(outcome.windows).length > 0 && (
        <>
          <SectionHead icon="📊" title="Outcome Windows" />
          <div style={{ fontSize: 9, color: 'var(--blue-dim)', marginBottom: 6, fontStyle: 'italic' }}>
            Prices are delayed spot checks, not exact historical prices at each minute.
          </div>
          <OutcomeWindows windows={outcome.windows} />
        </>
      )}

      {/* ── AI Review ── */}
      <SectionHead icon="🧠" title="AI Review" />

      {(journalState === 'idle' || journalState === 'reviewing') && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--blue-dim)', marginBottom: 8 }}>
            {outcome?.is_complete
              ? 'Evaluation complete — ready to generate AI review.'
              : 'Evaluation in progress. You can generate a review now or wait.'}
          </div>
          <button
            onClick={() => { setJournalState('reviewing'); onTriggerReview(snapshot.snapshot_id).then(r => { if (r) { setDraftText(r.journal_draft ?? ''); setJournalState('draft'); } else { setJournalState('idle'); } }); }}
            disabled={reviewLoading}
            style={{ ...btnBase, color: 'var(--cyan)', borderColor: 'var(--cyan)', opacity: reviewLoading ? 0.5 : 1 }}
          >
            {reviewLoading ? 'Generating…' : 'Generate AI Review'}
          </button>
          {reviewError && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 6 }}>{reviewError}</div>}
        </div>
      )}

      {review && journalState !== 'idle' && journalState !== 'reviewing' && (
        <div>
          {/* Quality badges */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 12 }}>
            {[
              ['Thesis Quality',      review.thesis_quality],
              ['Setup Quality',       review.setup_quality],
              ['Execution Alignment', review.execution_alignment],
              ['Conviction Accuracy', review.confidence_accuracy],
              ['Primary Driver',      review.primary_driver],
              ['Failure Driver',      review.failure_driver],
            ].map(([label, value]) => value && (
              <div key={label}>
                <span style={lbl(label)} />
                <Badge value={value} colorMap={QUALITY_COLOR} />
              </div>
            ))}
          </div>

          {review.lesson && (
            <div style={{ margin: '10px 0', padding: '8px 12px', border: '1px solid var(--cyan)', borderRadius: 4, background: 'var(--cyan)0d' }}>
              <span style={lbl('Lesson')} />
              <div style={{ fontSize: 11, color: 'var(--cyan)', lineHeight: 1.6 }}>{review.lesson}</div>
            </div>
          )}

          {/* ── Journal workflow ── */}
          <SectionHead icon="📝" title="Journal Draft" />

          {journalState === 'accepted' && (
            <div style={{ fontSize: 10, color: 'var(--green)', padding: '6px 0' }}>
              ✓ Journal entry accepted and saved.
            </div>
          )}

          {journalState === 'discarded' && (
            <div style={{ fontSize: 10, color: 'var(--blue-dim)', padding: '6px 0' }}>
              Draft discarded.{' '}
              <button onClick={() => setJournalState('draft')} style={{ ...btnBase, fontSize: 9, padding: '1px 6px' }}>Restore</button>
            </div>
          )}

          {(journalState === 'draft' || journalState === 'editing') && (
            <>
              {/* Minimal user inputs */}
              <JournalInputForm inputs={inputs} onChange={patchInputs} />

              {/* Draft text */}
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={lbl('Draft')} />
                  <button onClick={handleCopy} style={{ ...btnBase, fontSize: 9, padding: '1px 8px' }}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <textarea
                  value={draftText}
                  onChange={e => { setDraftText(e.target.value); setJournalState('editing'); }}
                  rows={4}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'var(--surface2)', border: '1px solid var(--border)',
                    color: 'var(--text)', ...mono, fontSize: 11,
                    padding: '6px 8px', borderRadius: 3, resize: 'vertical' as const,
                    lineHeight: 1.6,
                  }}
                />
              </div>

              {/* Accept / Discard */}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  onClick={handleAccept}
                  disabled={saving || !inputs.user_action || !inputs.followed_plan}
                  title={!inputs.user_action ? 'Select whether you took the trade' : !inputs.followed_plan ? 'Answer whether you followed the plan' : ''}
                  style={{
                    ...btnBase, color: 'var(--green)', borderColor: 'var(--green)',
                    background: 'var(--green)18',
                    opacity: (saving || !inputs.user_action || !inputs.followed_plan) ? 0.4 : 1,
                    cursor: (saving || !inputs.user_action || !inputs.followed_plan) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Saving…' : '✓ Accept'}
                </button>
                <button
                  onClick={handleDiscard}
                  disabled={saving}
                  style={{ ...btnBase, color: 'var(--red)', borderColor: 'var(--red)', opacity: saving ? 0.4 : 1 }}
                >
                  ✕ Discard
                </button>
              </div>

              {/* Required fields hint */}
              {(!inputs.user_action || !inputs.followed_plan) && (
                <div style={{ fontSize: 9, color: 'var(--amber)', marginTop: 6 }}>
                  Required before accepting:{' '}
                  {!inputs.user_action ? '"Did you take it?" ' : ''}
                  {!inputs.followed_plan ? '"Did you follow the plan?"' : ''}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AIReviewPanel;
